import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, AlertTriangle, X, ListChecks, Pause } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PeakEngine from "@/components/PeakEngine";
import SquadFilm from "@/components/SquadFilm";
import { ScenarioPanel } from "@/components/plan/ScenarioPanel";

export default function JobsTray() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [detail, setDetail] = useState(null);
  const openDetail = (j) => { setDetail({ job: j, loading: true }); api.get(`/jobs/${j.job_id}/charges`).then((r) => setDetail(r.data)).catch(() => setDetail({ job: j, charges: [], total: 0 })); };

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/jobs");
        if (!stop) setJobs((data.jobs || []).filter((j) => !j.dismissed));
      } catch { /* not signed in yet */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const running = jobs.filter((j) => j.status === "running");
  const canOpen = (j) => j.status === "done" && (j.meta?.peak_job_id || j.meta?.analysis_id || j.meta?.scenario_id);
  const visible = jobs.slice(0, 6);
  if (visible.length === 0) return null;

  const dismiss = async (j) => {
    setJobs((v) => v.filter((x) => x.job_id !== j.job_id));
    try { await api.post(`/jobs/${j.job_id}/dismiss`); } catch { /* ignore */ }
  };

  return (
    <div className="fixed bottom-20 right-4 z-[65] w-[min(92vw,340px)]" data-testid="jobs-tray">
      <button data-testid="jobs-tray-toggle" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-foreground text-background text-xs hover:bg-neutral-800 transition-colors">
        {running.length ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />}
        <span className="flex-1 text-left">
          {[running.length && `${running.length} running`, visible.filter((j) => j.status === "paused").length && `${visible.filter((j) => j.status === "paused").length} paused`, visible.filter((j) => !["running", "paused"].includes(j.status)).length && `${visible.filter((j) => !["running", "paused"].includes(j.status)).length} finished`].filter(Boolean).join(" · ") || "No jobs"}
        </span>
        <span className="opacity-70">{open ? "hide" : "show"}</span>
      </button>
      {open && (
        <div className="border-x border-b border-foreground bg-background max-h-[45vh] overflow-y-auto">
          {visible.map((j) => (
            <div key={j.job_id} role="button" tabIndex={0} onClick={() => openDetail(j)} onKeyDown={(e) => e.key === "Enter" && openDetail(j)} className="p-3 border-b border-border last:border-0 cursor-pointer hover:bg-surface" data-testid={`job-${j.job_id}`}>
              <div className="flex items-start gap-2">
                {j.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin mt-0.5" />}
                {j.status === "done" && (/with gaps/.test(j.message || "") ? <AlertTriangle data-testid={`job-gaps-${j.job_id}`} className="w-3.5 h-3.5 text-traffic-amber mt-0.5" /> : <Check className="w-3.5 h-3.5 text-traffic-green mt-0.5" />)}
                {j.status === "error" && <AlertTriangle className="w-3.5 h-3.5 text-traffic-red mt-0.5" />}
                {j.status === "paused" && <Pause className="w-3.5 h-3.5 text-traffic-amber mt-0.5" />}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{j.title}</p>
                  <p className="text-[11px] text-muted-foreground leading-snug">{j.message}</p>
                </div>
                <button data-testid={`job-dismiss-${j.job_id}`} onClick={(e) => { e.stopPropagation(); dismiss(j); }} aria-label="Dismiss"
                  className="text-muted-foreground hover:text-foreground transition-colors"><X className="w-3 h-3" /></button>
              </div>
              {j.status === "paused" && j.meta?.film_job_id && <button data-testid={`job-resume-${j.job_id}`} onClick={(e) => e.stopPropagation() || api.post(`/squad/media/jobs/${j.meta.film_job_id}/resume`).catch((e) => toast.error(e?.response?.data?.detail || "Could not resume"))} className="mt-2 mr-2 text-xs px-3 py-1.5 border border-border">Resume</button>}
              {j.status === "done" && /with gaps/.test(j.message || "") && j.meta?.film_job_id && <button data-testid={`job-retry-${j.job_id}`} onClick={(e) => e.stopPropagation() || api.post(`/squad/media/jobs/${j.meta.film_job_id}/retry-failed`).then(() => toast.success("Retrying the failed parts")).catch((e) => toast.error(e?.response?.data?.detail || "Could not retry"))} className="mt-2 mr-2 text-xs px-3 py-1.5 border border-traffic-amber">Retry failed parts</button>}
              {canOpen(j) && <button data-testid={`job-open-${j.job_id}`} onClick={(e) => { e.stopPropagation(); setOpen(false); setView(j); }} className="mt-2 text-xs px-3 py-1.5 bg-foreground text-background">Open result</button>}
              {j.status === "running" && (
                <div className="h-1 w-full bg-surface border border-border mt-2 overflow-hidden">
                  <div className="h-full bg-foreground transition-[width] duration-700"
                    style={{ width: `${Math.max(4, j.progress || 0)}%` }} />
                </div>
              )}
            </div>
          ))}
          <p className="px-3 py-2 text-[10px] text-muted-foreground">
            Long reads keep running if you leave the page · come back any time.
          </p>
        </div>
      )}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="job-detail-dialog" className="max-w-xl">
          <DialogHeader><DialogTitle>{detail?.job?.title}</DialogTitle><DialogDescription>{`${detail?.job?.status || ""} · started ${detail?.job?.created_at ? new Date(detail.job.created_at).toLocaleString() : ""}`}</DialogDescription></DialogHeader>
          <p className="text-sm" data-testid="job-detail-message">{detail?.job?.message}</p>
          <div className="border border-border">
            <div className="flex justify-between px-3 py-2 bg-surface text-xs uppercase tracking-wider"><span>What you paid for</span><span data-testid="job-detail-total">{detail?.loading ? "…" : `${detail?.total || 0} credits`}</span></div>
            <ul className="divide-y divide-border max-h-72 overflow-y-auto text-sm">
              {(detail?.charges || []).map((c, i) => (
                <li key={i} className="flex justify-between gap-3 px-3 py-2" data-testid={`job-charge-${i}`}><span>{c.what}{c.detail && <span className="block text-[11px] text-muted-foreground">{c.detail}</span>}</span><span className="tabular-nums whitespace-nowrap">{`${c.credits} cr`}<span className="block text-[11px] text-muted-foreground">{new Date(c.at).toLocaleTimeString()}</span></span></li>
              ))}
              {!detail?.loading && !(detail?.charges || []).length && <li className="px-3 py-2 text-muted-foreground" data-testid="job-charge-none">No credits charged for this job.</li>}
            </ul>
          </div>
          {detail?.job && canOpen(detail.job) && <button data-testid="job-detail-open" onClick={() => { const j = detail.job; setDetail(null); setOpen(false); setView(j); }} className="text-sm px-4 py-2 bg-foreground text-background self-start">Open result</button>}
        </DialogContent>
      </Dialog>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent data-testid="job-result-dialog" className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{view?.title}</DialogTitle><DialogDescription className="sr-only">{view?.title}</DialogDescription></DialogHeader>
          {view?.meta?.peak_job_id && <PeakEngine initialJobId={view.meta.peak_job_id} athleteId={view.meta.athlete_id} squad={view.meta.squad} academyId={view.meta.academy_id || ""} squadMode={!!view.meta.squad} />}
          {view?.meta?.scenario_id && <ScenarioPanel readOnly squad="" onlyId={view.meta.scenario_id} />}
          {view?.meta?.analysis_id && <SquadFilm resultOnly initialAnalysisId={view.meta.analysis_id} squad={view.meta.squad || ""} academyId={view.meta.academy_id || ""} athleteId={view.meta.athlete_id || ""} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
